import { OrderUnitOfMeasure, RateType } from '@treadinc/horizon-api-spec';

import {
  QuoteLineItemsProject,
  QuoteMaterialRate,
  QuoteMaterialTotal,
  QuoteRateCard,
} from '~components/Project/QuotePdf/buildQuoteLineItems';
import { SiteRateScheduleDTO } from '~pages/Sales/Projects/components/SiteRatesForm';
import { Nullable } from '~types/Nullable';

import { ProjectFormSchemaInterface } from '../projectFormSchema';

type FormOption = Nullable<{ label?: Nullable<string>; value?: Nullable<string> }>;

function toNamedRecord(option: FormOption) {
  if (!option?.value) return null;
  return { id: option.value, name: option.label ?? '' };
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function toQuoteRateCard(siteRate: SiteRateScheduleDTO): QuoteRateCard {
  return {
    customerRateType:
      (siteRate.customerRate?.type?.value as RateType | undefined) ?? null,
    customerRateValue: toNullableNumber(siteRate.customerRate?.rate),
    dropoffSite: toNamedRecord(siteRate.dropoffSite),
    equipmentType: toNamedRecord(siteRate.equipmentType),
    id: siteRate.id ?? siteRate.clientKey ?? '',
    material: toNamedRecord(siteRate.material),
    name: siteRate.name ?? null,
    pickupSite: toNamedRecord(siteRate.pickupSite),
    quantity: toNullableNumber(siteRate.quantity),
  };
}

function toQuoteMaterialTotals(
  materialTotals: ProjectFormSchemaInterface['projectMaterialTotals'],
) {
  const rows: QuoteMaterialTotal[] = [];
  (materialTotals ?? []).forEach((materialTotal, index) => {
    if (!materialTotal || materialTotal.isDeleted) return;
    const quantity = String(materialTotal.totalQuantity ?? '').trim();
    rows.push({
      id: materialTotal.id || `form-material-total-${index}`,
      material: materialTotal.material?.id
        ? {
            id: materialTotal.material.id,
            name: materialTotal.material.name ?? '',
          }
        : null,
      totalQuantity: quantity,
      unitOfMeasure: materialTotal.unitOfMeasure?.id
        ? { id: materialTotal.unitOfMeasure.id }
        : null,
    });
  });
  return rows;
}

// The preview quotes what is on screen, so material rates come from the form
// rows — including ones the user has added but not saved. A row with no server
// id still needs a distinct key, or two unsaved rows would collide in the
// helper's per-row bookkeeping — the same reason the material totals above
// fall back to their index.
function toQuoteMaterialRates(
  materialRates: ProjectFormSchemaInterface['materialRates'],
): QuoteMaterialRate[] {
  const rows: QuoteMaterialRate[] = [];
  (materialRates ?? []).forEach((materialRate, index) => {
    if (!materialRate || materialRate.isDeleted) return;
    if (!materialRate.material?.id) return;
    rows.push({
      id: materialRate.id || `form-material-rate-${index}`,
      material: {
        id: materialRate.material.id,
        name: materialRate.material.name ?? '',
      },
      site: materialRate.materialRateSite?.id
        ? {
            id: materialRate.materialRateSite.id,
            name: materialRate.materialRateSite.name ?? '',
          }
        : null,
      sellPrice: toNullableNumber(materialRate.materialRateSellPrice),
      unitOfMeasure:
        (materialRate.unitOfMeasure?.id as OrderUnitOfMeasure | undefined) ?? null,
    });
  });
  return rows;
}

export function buildQuotePreviewInput(
  values: ProjectFormSchemaInterface,
  siteRates: SiteRateScheduleDTO[],
): {
  project: QuoteLineItemsProject;
  siteRateSchedules: QuoteRateCard[];
  materialRates: QuoteMaterialRate[];
} {
  return {
    project: {
      materialRateItemized: values.materialRateItemized ?? false,
      projectMaterialTotals: toQuoteMaterialTotals(values.projectMaterialTotals),
      taxAddOns: values.taxAddOns ?? null,
      taxFreight: values.taxFreight ?? null,
      taxMaterial: values.taxMaterial ?? null,
      taxRate: values.taxRate ?? null,
    },
    siteRateSchedules: siteRates.map(toQuoteRateCard),
    materialRates: toQuoteMaterialRates(values.materialRates),
  };
}
